// KPI grid satır sanallaştırma + sütun genişliği planı — kpi-grid-nav.ts'ten salt taşıma (≤600
// satır kuralı; kırılım satır türleri o dosyada büyüdü). kpi-grid-nav bu modülü yeniden dışa aktarır.

import type { GridRowKind } from "./kpi-grid-breakdown";
import type { GridColumn } from "./kpi-grid-nav";

// ─── Satır sanallaştırma (TODO-KPI-07) ───────────────────────────────────────
// Bağımlılıksız basit pencere: kabın scrollTop/yüksekliğinden görünür satır aralığı (+tampon);
// üst/alt doldurucu yükseklikleri satır yüksekliklerinden türetilir. Satır türüne göre yükseklik
// (`gridRowHeights` — portalda grup bandı 40, diğer satırlar 56 px) farklıysa önek toplamlı ofsetler
// kullanılır (bant başına kayma yok); hepsi eşitse eski sabit yükseklik yolu (byte-eşit pencere).
// ≤ VIRTUAL_THRESHOLD satırda devreye girmez (tüm satırlar — DOM byte-eşit).

/** Birim sütunu sabit genişliği (px). */
export const UNIT_COL_WIDTH = 80;
/** Değişim sütunu sabit genişliği (px). */
export const CHANGE_COL_WIDTH = 90;
/** Portal durum sütunu sabit genişliği (px) — KpiGridHead ile aynı. */
export const STATUS_COL_WIDTH = 180;

/**
 * Finar #5 (2026-10-30): `<colgroup>` genişlik planı. Sıra satır hücreleriyle birebir: ad, birim,
 * `columns` (yıl/işletme/konsolide — `null` = kalan alanı eşit paylaşır), değişim?, durum?.
 * Tablo `width:100% + tableLayout:fixed` ile çizilince genişlik render edilen (sanal) satırların
 * içeriğine değil bu plana bağlıdır → kaydırmada sütunlar zıplamaz.
 */
export function gridColumnWidths(args: {
  columns: readonly GridColumn[];
  nameColWidth: number;
  showChangeCol: boolean;
  statusCol: boolean;
}): (number | null)[] {
  const out: (number | null)[] = [args.nameColWidth, UNIT_COL_WIDTH];
  for (const _c of args.columns) out.push(null);
  if (args.showChangeCol) out.push(CHANGE_COL_WIDTH);
  if (args.statusCol) out.push(STATUS_COL_WIDTH);
  return out;
}

export const GRID_ROW_HEIGHT = 36;
/** Final review m7: portalda (clientView) hücre altında durum rozeti / "danışman düzeltti" satırı
 *  var (~55 px) — sanallaştırma doldurucuları ve kaydırma hedefi aynı sabit yükseklikle hesaplanır,
 *  notu olmayan satırlar da bu yükseklikte (tutarlı pencere, içerik zıplamaz). */
export const PORTAL_GRID_ROW_HEIGHT = 56;

/** Kırılım grup bandı (DR9): danışmanda satırla aynı 36 px, portalda 40 px (satır 56 px). */
export const PORTAL_GROUP_BAND_HEIGHT = 40;

export function gridRowHeight(clientView: boolean, kind?: GridRowKind): number {
  if (kind === "group") return clientView ? PORTAL_GROUP_BAND_HEIGHT : GRID_ROW_HEIGHT;
  return clientView ? PORTAL_GRID_ROW_HEIGHT : GRID_ROW_HEIGHT;
}

/** Satır başına yükseklik (render edilen `<tr>` ile aynı kural) — sanallaştırma ofsetleri buradan. */
export function gridRowHeights(
  rows: readonly { kind?: GridRowKind | undefined }[],
  clientView: boolean,
): number[] {
  return rows.map((r) => gridRowHeight(clientView, r.kind));
}

/** Hepsi aynıysa o yükseklik (sabit yol), farklıysa null. Boş dizi → null. */
function uniformHeight(heights: readonly number[]): number | null {
  const first = heights[0];
  if (first === undefined) return null;
  return heights.every((h) => h === first) ? first : null;
}

/** Önek toplamları: `offsets[i]` = i. satırın içerik üst kenarı; `offsets[n]` = toplam yükseklik. */
export function rowOffsets(heights: readonly number[]): number[] {
  const out = new Array<number>(heights.length + 1);
  out[0] = 0;
  for (let i = 0; i < heights.length; i++) out[i + 1] = out[i]! + heights[i]!;
  return out;
}

/** `y` pikselini içeren satır (ikili arama); `y` toplamı aşarsa son satır. */
function rowAt(offsets: readonly number[], y: number): number {
  const n = offsets.length - 1;
  let lo = 0;
  let hi = n - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (offsets[mid]! <= y) lo = mid;
    else hi = mid - 1;
  }
  return Math.max(0, lo);
}

type NormalizedHeights = { fixed: number } | { heights: readonly number[]; offsets: number[] };

/** TODO-KPI-119: dizi başına bir kez hesaplanır (kaydırma render'ı O(n) ofset kurmaz); `KpiGrid`
 *  diziyi `useMemo` ile sabit tutar, dizi değişince eski kayıt çöp toplanır. */
const NORMALIZED_CACHE = new WeakMap<readonly number[], NormalizedHeights>();

/** Çağıranın verdiği yükseklik → sabit sayı ya da satır başına dizi (eşitse sabite iner). */
export function normalizeRowHeights(
  rowHeight: number | readonly number[] | undefined,
): NormalizedHeights {
  if (rowHeight === undefined) return { fixed: GRID_ROW_HEIGHT };
  if (typeof rowHeight === "number") return { fixed: rowHeight };
  const hit = NORMALIZED_CACHE.get(rowHeight);
  if (hit) return hit;
  const u = uniformHeight(rowHeight);
  let out: NormalizedHeights;
  if (u !== null) out = { fixed: u };
  else if (rowHeight.length === 0) out = { fixed: GRID_ROW_HEIGHT };
  else out = { heights: rowHeight, offsets: rowOffsets(rowHeight) };
  NORMALIZED_CACHE.set(rowHeight, out);
  return out;
}

const MISMATCH_SEEN = new Set<string>();

/**
 * TODO-KPI-119: yükseklik dizisi satır sayısıyla uyuşmazsa pencere sessizce 36 px sabit yola düşer
 * (yanlış doldurucu) — geliştirmede bir kez `console.error` (üretimde sessiz, davranış aynı).
 */
function warnHeightMismatch(heights: number, rows: number): void {
  if (!import.meta.env.DEV) return;
  const key = `${heights.toString()}:${rows.toString()}`;
  if (MISMATCH_SEEN.has(key)) return;
  MISMATCH_SEEN.add(key);
  console.error(
    `kpi-grid-virtual: satır yüksekliği dizisi (${heights.toString()}) satır sayısıyla (${rows.toString()}) uyuşmuyor; sabit ${GRID_ROW_HEIGHT.toString()} px yola düşüldü`,
  );
}

/** Test yardımcısı: uyuşmazlık uyarısı önbelleğini sıfırlar. */
export function resetHeightMismatchWarnings(): void {
  MISMATCH_SEEN.clear();
}

/** Satırın içerik üst kenarı (başlık hariç) — `focusCell` ortalama hedefi. */
export function rowTop(
  rowIndex: number,
  rowHeight: number | readonly number[] | undefined,
): number {
  const h = normalizeRowHeights(rowHeight);
  if ("fixed" in h) return rowIndex * h.fixed;
  return h.offsets[Math.max(0, Math.min(rowIndex, h.heights.length))]!;
}

/** Kaydırma çerçevesi eşiği: en kısa satır (tampon yarısı × bu kadar kayınca pencere kayar). */
export function minRowHeight(rowHeight: number | readonly number[] | undefined): number {
  const h = normalizeRowHeights(rowHeight);
  if ("fixed" in h) return h.fixed;
  return h.heights.reduce((m, x) => Math.min(m, x), Number.POSITIVE_INFINITY);
}
export const VIRTUAL_THRESHOLD = 80;
export const VIRTUAL_BUFFER = 20;

export interface VirtualWindow {
  /** render edilen satır aralığı [start, end) — GridRow indeksleri (navReducer ile aynı) */
  start: number;
  end: number;
  topPad: number;
  bottomPad: number;
  active: boolean;
}

export function virtualWindow(args: {
  rowCount: number;
  scrollTop: number;
  viewportHeight: number;
  /** sticky başlık yüksekliği (içerik ofseti) */
  headerHeight?: number;
  /** sabit yükseklik ya da satır başına dizi (`gridRowHeights`) */
  rowHeight?: number | readonly number[];
  buffer?: number;
  threshold?: number;
}): VirtualWindow {
  const buffer = args.buffer ?? VIRTUAL_BUFFER;
  const threshold = args.threshold ?? VIRTUAL_THRESHOLD;
  const header = args.headerHeight ?? 0;
  const n = Math.max(0, args.rowCount);
  if (n <= threshold) return { start: 0, end: n, topPad: 0, bottomPad: 0, active: false };
  const h = normalizeRowHeights(args.rowHeight);
  if (!("fixed" in h) && h.heights.length === n) {
    const { offsets } = h;
    const y = Math.max(0, args.scrollTop - header);
    const first = rowAt(offsets, y);
    const last = rowAt(offsets, y + Math.max(0, args.viewportHeight));
    const start = Math.max(0, Math.min(n, first - buffer));
    const end = Math.max(start, Math.min(n, last + 1 + buffer));
    return {
      start,
      end,
      topPad: offsets[start]!,
      bottomPad: offsets[n]! - offsets[end]!,
      active: true,
    };
  }
  if (!("fixed" in h)) warnHeightMismatch(h.heights.length, n);
  const rowH = "fixed" in h ? h.fixed : GRID_ROW_HEIGHT;
  const first = Math.floor(Math.max(0, args.scrollTop - header) / rowH);
  const visible = Math.ceil(Math.max(0, args.viewportHeight) / rowH) + 1;
  const start = Math.max(0, Math.min(n, first - buffer));
  const end = Math.max(start, Math.min(n, first + visible + buffer));
  return { start, end, topPad: start * rowH, bottomPad: (n - end) * rowH, active: true };
}

/** Satırı görünür pencereye getiren scrollTop (zaten görünürse aynen döner). */
export function scrollTopForRow(args: {
  rowIndex: number;
  scrollTop: number;
  viewportHeight: number;
  headerHeight?: number;
  /** sabit yükseklik ya da satır başına dizi (`gridRowHeights`) */
  rowHeight?: number | readonly number[];
}): number {
  const h = normalizeRowHeights(args.rowHeight);
  const header = args.headerHeight ?? 0;
  const ownH = "fixed" in h ? h.fixed : (h.heights[args.rowIndex] ?? GRID_ROW_HEIGHT);
  const offset =
    "fixed" in h
      ? args.rowIndex * h.fixed
      : h.offsets[Math.max(0, Math.min(args.rowIndex, h.heights.length))]!;
  const top = header + offset; // içerik koordinatı
  const bottom = top + ownH;
  const viewTop = args.scrollTop + header; // sticky başlık altındaki ilk görünür piksel
  const viewBottom = args.scrollTop + args.viewportHeight;
  if (top < viewTop) return Math.max(0, top - header);
  if (bottom > viewBottom) return Math.max(0, bottom - args.viewportHeight);
  return args.scrollTop;
}
